import React, {Component} from "react";
import { GaAnalytics } from "../analytics";

/**
 * Values accessible by the GameContext's children. These need to be passed into the value property in the Provider in order to be accessible.
 * @param {number} currentEnergy - Current exhaustion level of the player, usually modified by selecting choices. As energy level may effect 
 * many different but unrelated components in the game (energy display, animations, global color effects, available choices), this data is provided 
 * by a GameContext.*/
export interface GameContextProps {
    currentEnergy: number,
    modifyEnergy: (change: number) => void,
    analytics?: GaAnalytics
}

/**
 * The Context for game state management. This context allows child components to easily access/modify data that nested components need 
 * (via useContext) without having to explicitly recieve it as a property from a parent component. Should be used sparingly, as it makes 
 * components that depend on it less reusable. */
export const GameContext = React.createContext<GameContextProps | null>(null);

/**The GameProvider allows all its child components to access its state data via useContext().*/
export class GameProvider extends Component<{children: React.ReactNode}, {energy: number, analytics?: GaAnalytics}> {
    constructor(props: any) {
        super(props);
        this.state = {
            energy: 10,
            analytics: undefined
        };
    }

    async componentDidMount() {
        await this.createAnalyticsInstance();
    }

    async createAnalyticsInstance() {
        const id = process.env.REACT_APP_GA_ID ?? "";
        const gaInstance = await GaAnalytics.create(id);
        this.setState({analytics: gaInstance})
    }

    modifyEnergy(change: number) {
        const { energy } = this.state;
        const maxEnergy = 10;
        const newEnergy = energy + change;
        this.setState({energy: this.clamp(newEnergy, 0, maxEnergy)});
    }

    clamp(num: number, min: number, max: number) {
        return Math.min(Math.max(num, min), max);
    }
 
    render() {
        const { energy, analytics } = this.state;
        const { children } = this.props;
        const data = {
            currentEnergy: energy,
            modifyEnergy: (change: number) => this.modifyEnergy(change),
            analytics: analytics
        };

        return (
            <GameContext.Provider value={data}>
                {children} 
            </GameContext.Provider>
        );
    }
}